import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environment';
import {
  AddNewUserProcessOpts,
  AssignProcessTypeOpts,
  ChangeUserDocStatusOpts,
  ChangeUserProcessStatusOpts,
  ChangeUserProcessTypeOpts,
  DeleteImmigrationOfficeVisitDate,
  DocumentComment,
  DocumentsGenerationStatus,
  EditUserProcess,
  EmailHTMLNotification,
  ExecuteProcessFlowActionOpts,
  ManagementUploadDecisionOpts,
  SearchUserActivityOpts,
  PaginatedResponse,
  PdfGenerationCurrentProgress,
  PdfGenerationsOverview,
  ProcessDocumentsList,
  SearchProcessesOfUser,
  SearchUsersProcessesPayload,
  SetDocumentsSigningModeOpts,
  SetMainStayProcessOfUserOpts,
  SetPersonalDetailsVerifiedPayload,
  SetVisitDateOpts,
  UpdateForeignerDocumentQuestionAnswerPayload,
  UpdatePersonalDetailsPayload,
  UpdateUserProcessServicesOpts,
  UserAsset,
  UserDocument,
  UserDocumentQuestionAnswer,
  UserProcess,
  UserProcessChange,
  UserProcessChangeType,
  ValidationConfig,
  PersonalDetails,
} from '@interfaces';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ManagementHttpService {
  private readonly http = inject(HttpClient);

  private API_URL = environment.API_URL;

  public searchUsersProcesses(
    payload: SearchUsersProcessesPayload
  ): Observable<PaginatedResponse<Partial<UserProcess>>> {
    const url = `${this.API_URL}/management/user-processes/search`;
    return this.http.post<PaginatedResponse<Partial<UserProcess>>>(url, payload);
  }

  public searchProcessesOfUser(
    payloadWithUserId: SearchProcessesOfUser
  ): Observable<PaginatedResponse<Partial<UserProcess>>> {
    const { userId, ...payload } = payloadWithUserId;
    const url = `${this.API_URL}/management/user/${userId}/user-processes/search-user-processes`;
    return this.http.post<PaginatedResponse<Partial<UserProcess>>>(url, payload);
  }

  public getUserProcessBasicData({ userProcessId }: { userProcessId: string }): Observable<Partial<UserProcess>> {
    const url = `${this.API_URL}/management/user-processes/${userProcessId}/basic-data`;
    return this.http.get<Partial<UserProcess>>(url);
  }

  public addNewUserProcess(payloadWithUserId: AddNewUserProcessOpts): Observable<Partial<UserProcess>> {
    const { userId, ...payload } = payloadWithUserId;
    const url = `${this.API_URL}/management/user/${userId}/user-processes/new`;
    return this.http.post<Partial<UserProcess>>(url, payload);
  }

  public assignProcessType(opts: AssignProcessTypeOpts): Observable<UserProcess> {
    const { userProcessId, foreignerId, ...payload } = opts;
    const url = `${this.API_URL}/management/user/${foreignerId}/user-processes/${userProcessId}/assign-process-type`;
    return this.http.patch<UserProcess>(url, payload);
  }

  public getUserProcess({ userId, userProcessId }: { userId: string; userProcessId: string }): Observable<UserProcess> {
    const url = `${this.API_URL}/management/user/${userId}/user-processes/${userProcessId}`;
    return this.http.get<UserProcess>(url);
  }

  public uploadDecision(opts: ManagementUploadDecisionOpts): Observable<UserProcess> {
    const { userId, userProcessId, ...body } = opts;
    const url = `${this.API_URL}/management/user/${userId}/user-processes/${userProcessId}/upload-decision`;
    return this.http.patch<UserProcess>(url, body);
  }

  public removeUserProcess(userProcessId: string): Observable<void> {
    const url = `${this.API_URL}/management/user-process/${userProcessId}`;
    return this.http.delete<void>(url);
  }

  public restoreUserProcess(userProcessId: string): Observable<Partial<UserProcess>> {
    const url = `${this.API_URL}/management/user-process/${userProcessId}/restore`;
    return this.http.patch<Partial<UserProcess>>(url, {});
  }

  public getPersonalDetailsFullData(userProcessId: string): Observable<PersonalDetails> {
    const url = `${this.API_URL}/personal-details/user-process/${userProcessId}/personal-details/full`;
    return this.http.get<PersonalDetails>(url);
  }

  public updateUserProcessBasicData(
    userProcessId: string,
    payloadWithUserId: EditUserProcess
  ): Observable<Partial<UserProcess>> {
    const { userId, ...payload } = payloadWithUserId;
    const url = `${this.API_URL}/management/user/${userId}/user-processes/${userProcessId}/update-basic-data`;
    return this.http.patch<Partial<UserProcess>>(url, payload);
  }

  public getPersonalDetailsValidationConfig(
    foreignerId: string,
    userProcessId: string
  ): Observable<{ members: ValidationConfig; all: ValidationConfig }> {
    const url = `${this.API_URL}/management/user/${foreignerId}/user-processes/${userProcessId}/get-personal-details-validation-config`;
    return this.http.get<{ members: ValidationConfig; all: ValidationConfig }>(url);
  }

  public updatePersonalDetailsInUserProcess(payload: UpdatePersonalDetailsPayload): Observable<UserProcess> {
    const { personalDetails, userId, userProcessId } = payload;
    const url = `${this.API_URL}/management/user/${userId}/user-processes/${userProcessId}/update-personal-details`;
    return this.http.patch<UserProcess>(url, personalDetails);
  }

  public setPersonalDetailsVerification(payload: SetPersonalDetailsVerifiedPayload): Observable<UserProcess> {
    const { userId, userProcessId, verified } = payload;
    const url = `${this.API_URL}/management/user/${userId}/user-processes/${userProcessId}/set-personal-details-verified`;
    return this.http.patch<UserProcess>(url, { verified });
  }

  public confirmVoivoChange(userId: string, userProcessId: string): Observable<boolean> {
    const url = `${this.API_URL}/management/user/${userId}/user-processes/${userProcessId}/confirm-voivo-change`;
    return this.http.get<boolean>(url);
  }

  public executeProcessFlowAction(opts: ExecuteProcessFlowActionOpts): Observable<UserProcess> {
    const { userId, userProcessId, actionName } = opts;
    const url = `${this.API_URL}/management/user/${userId}/user-processes/${userProcessId}/process-flow-actions/${actionName}`;
    return this.http.patch<UserProcess>(url, {});
  }

  public setDocumentsSigningMode(opts: SetDocumentsSigningModeOpts): Observable<UserProcess> {
    const { userId, userProcessId, mode } = opts;
    const url = `${this.API_URL}/management/user/${userId}/user-processes/${userProcessId}/documents-signing-mode`;
    return this.http.post<UserProcess>(url, { mode });
  }

  public getUserProcessDocuments({
    userId,
    userProcessId,
  }: {
    userId: string;
    userProcessId: string;
  }): Observable<ProcessDocumentsList> {
    const url = `${this.API_URL}/management/user/${userId}/user-processes/${userProcessId}/processDocuments`;
    return this.http.get<ProcessDocumentsList>(url);
  }

  public setUserDocumentStatus(opts: ChangeUserDocStatusOpts): Observable<UserDocument> {
    const { foreignerId, status, userDocId, userProcessId } = opts;
    const url = `${this.API_URL}/management/user/${foreignerId}/user-processes/${userProcessId}/user-document/${userDocId}/status`;
    const body = { status };
    return this.http.put<UserDocument>(url, body);
  }

  public changeUserProcessStatus({
    userId,
    newUserProcessStatus,
    userProcessId,
  }: ChangeUserProcessStatusOpts): Observable<UserProcess> {
    const url = `${this.API_URL}/management/user/${userId}/user-processes/${userProcessId}/processStatus`;
    return this.http.patch<UserProcess>(url, { newUserProcessStatus });
  }

  public generateDocumentsNow(foreignerId: string, userProcessId: string): Observable<void> {
    const url = `${this.API_URL}/management/user/${foreignerId}/user-processes/${userProcessId}/generate-documents-now`;
    return this.http.get<void>(url);
  }

  public generateChecklist(foreignerId: string, userProcessId: string): Observable<void> {
    const url = `${this.API_URL}/management/user/${foreignerId}/user-processes/${userProcessId}/generate-checklist`;
    return this.http.get<void>(url);
  }

  public getDocumentsGenerationStatus(userId: string, userProcessId: string): Observable<DocumentsGenerationStatus> {
    const url = `${this.API_URL}/management/user/${userId}/user-processes/${userProcessId}/documents-generation-status`;
    return this.http.get<DocumentsGenerationStatus>(url);
  }

  public getPdfGenerations(userId: string, userProcessId: string): Observable<PdfGenerationsOverview> {
    const url = `${this.API_URL}/pdf-generation/user/${userId}/user-processes/${userProcessId}/generations`;
    return this.http.get<PdfGenerationsOverview>(url);
  }

  public getPdfGenerationCurrentProgress(
    userId: string,
    userProcessId: string
  ): Observable<PdfGenerationCurrentProgress> {
    const url = `${this.API_URL}/pdf-generation/user/${userId}/user-processes/${userProcessId}/generations/current-progress`;
    return this.http.get<PdfGenerationCurrentProgress>(url);
  }

  public printAllDocumentsOfForeinger(userId: string, userProcessId: string): Observable<any> {
    const url = `${this.API_URL}/management/user/${userId}/user-processes/${userProcessId}/print-all-documents`;
    return this.http.get(url, { responseType: 'blob' });
  }

  public printChecklist(userId: string, userProcessId: string): Observable<any> {
    const url = `${this.API_URL}/management/user/${userId}/user-processes/${userProcessId}/print-checklist`;
    return this.http.get(url, { responseType: 'blob' });
  }

  public areDocsGenerated(userId: string, userProcessId: string): Observable<{ generated: boolean }> {
    const url = `${this.API_URL}/management/user/${userId}/user-processes/${userProcessId}/are-docs-generated`;
    return this.http.get<{ generated: boolean }>(url);
  }

  public getDocumentComments(userDocumentId: string, userProcessId: string): Observable<DocumentComment[]> {
    const url = `${this.API_URL}/document-comments/document/${userDocumentId}/userProcess/${userProcessId}`;
    return this.http.get<DocumentComment[]>(url);
  }

  public addDocumentComment(payload: DocumentComment, userProcessId: string): Observable<DocumentComment> {
    const url = `${this.API_URL}/document-comments/document/${payload.userDocumentId}/userProcess/${userProcessId}`;
    const body = { content: payload.content };
    return this.http.post<DocumentComment>(url, body);
  }

  public updateForeignerDocumentQuestionAnswer(
    payload: UpdateForeignerDocumentQuestionAnswerPayload
  ): Observable<UserDocumentQuestionAnswer> {
    const { userProcessId, userDocumentId, userDocumentAnswerId, answer } = payload;
    const url = `${this.API_URL}/user-documents/${userProcessId}/${userDocumentId}/${userDocumentAnswerId}`;
    const body = { answer };
    return this.http.patch<UserDocumentQuestionAnswer>(url, body);
  }

  public setVisitDate(opts: SetVisitDateOpts): Observable<UserProcess> {
    const { userId, userProcessId, ...body } = opts;
    const url = `${this.API_URL}/management/user/${userId}/user-processes/${userProcessId}/set-visit-date`;

    return this.http.post<UserProcess>(url, body);
  }

  public deleteImmigrationOfficeVisitDate(opts: DeleteImmigrationOfficeVisitDate): Observable<UserProcess> {
    const { userId, userProcessId, visitDate } = opts;
    const url = `${this.API_URL}/management/user/${userId}/user-processes/${userProcessId}/delete-immigration-office-visit-date`;
    const body = { visitDate };
    return this.http.delete<UserProcess>(url, { body });
  }

  public sendAppointmentReminder(opts: { userId: string; userProcessId: string }): Observable<{ emailSent: boolean }> {
    const { userId, userProcessId } = opts;
    const url = `${this.API_URL}/management/user/${userId}/user-processes/${userProcessId}/send-appointment-reminder`;
    return this.http.post<{ emailSent: boolean }>(url, {});
  }

  public sendImmigrofVisitReminder(opts: {
    userId: string;
    userProcessId: string;
  }): Observable<{ emailSent: boolean }> {
    const { userId, userProcessId } = opts;
    const url = `${this.API_URL}/management/user/${userId}/user-processes/${userProcessId}/send-immigrof-visit-reminder`;
    return this.http.post<{ emailSent: boolean }>(url, {});
  }

  public getUserProcessChanges(
    userProcessId: string,
    changeTypes?: UserProcessChangeType[],
    changeAuthorId?: string
  ): Observable<UserProcessChange[]> {
    const url = `${this.API_URL}/uph/search`;
    const body = { userProcessId } as any;
    if (changeTypes) {
      body.changeTypes = changeTypes;
    }
    if (changeAuthorId) {
      body.changeAuthorId = changeAuthorId;
    }

    return this.http.post<UserProcessChange[]>(url, body);
  }

  public getUserActivity(payload: SearchUserActivityOpts): Observable<PaginatedResponse<UserProcessChange>> {
    const url = `${this.API_URL}/uph/search/user-activity`;
    return this.http.post<PaginatedResponse<UserProcessChange>>(url, payload);
  }

  public downloadErrorScreenshot(screenshotPath: string): Observable<any> {
    const url = `${this.API_URL}/uph/download/error-screenshot`;
    return this.http.post(url, { screenshotPath }, { responseType: 'blob' });
  }

  public downloadRecording(recordingPath: string): Observable<any> {
    const url = `${this.API_URL}/uph/download/recording`;
    return this.http.post(url, { recordingPath }, { responseType: 'blob' });
  }

  public getItemsSentForFinalReview(opts: { userProcessId: string }): Observable<UserAsset[]> {
    const url = `${this.API_URL}/user-assets/user-process/${opts.userProcessId}/documents-sent-to-review`;
    return this.http.get<UserAsset[]>(url);
  }

  public getDecisionDocuments(opts: { userProcessId: string }): Observable<UserAsset[]> {
    const url = `${this.API_URL}/user-assets/user-process/${opts.userProcessId}/decision-documents`;
    return this.http.get<UserAsset[]>(url);
  }

  public downloadItemSentForFinalReview(opts: { userProcessId: string; userAssetId: string }): Observable<any> {
    const url = `${this.API_URL}/user-assets/user-process/${opts.userProcessId}/document-sent-for-final-review/${opts.userAssetId}`;
    return this.http.get(url, { responseType: 'blob' });
  }

  public updateDocumentsReviewComment(opts: {
    userProcessId: string;
    userId: string;
    comment: string;
  }): Observable<UserProcess> {
    const { comment, userId, userProcessId } = opts;
    const url = `${this.API_URL}/management/user/${userId}/user-processes/${userProcessId}/update-documents-review-comment`;
    const body = { documentsReviewComment: comment };
    return this.http.patch<UserProcess>(url, body);
  }

  public updateIncludedServices(opts: UpdateUserProcessServicesOpts): Observable<UserProcess> {
    const { userId, userProcessId, payload } = opts;
    const url = `${this.API_URL}/management/user/${userId}/user-processes/${userProcessId}/update-services`;
    return this.http.patch<UserProcess>(url, payload);
  }

  public setMainStayProcess(opts: SetMainStayProcessOfUserOpts): Observable<UserProcess> {
    const { userId, userProcessId, isMainStayProcessOfUser } = opts;
    const url = `${this.API_URL}/management/user/${userId}/user-processes/${userProcessId}/set-main-stay-process`;
    return this.http.patch<UserProcess>(url, { isMainStayProcessOfUser });
  }

  public isAnyProcessOfMineMissingAssistant(): Observable<boolean> {
    const url = `${this.API_URL}/management/user-processes/is-any-of-mine-missing-assistant`;
    return this.http.get<boolean>(url);
  }

  public sendFollowUpEmail(
    usersId: string[],
    forcedMessageContent: EmailHTMLNotification
  ): Observable<{ userID: string; success: boolean; error?: string }[]> {
    const url = `${this.API_URL}/users/foreigner/send-follow-up-emails`;
    const payload = { userIds: usersId, messageContent: forcedMessageContent };

    return this.http.post<{ userID: string; success: boolean; error?: string }[]>(url, payload);
  }
}
